import React from "react";
import { P } from "common/components/Paragraph";
import { useTracking } from "react-tracking";
import { useDependentState } from "common/hooks/useDependentState";
import { Map } from "immutable";
import { TrackingData } from "common/helpers/Analytics";
import { useLocation } from "react-router";

interface AccordionProps {
  children: React.ReactNode;
  title: string;
  description: string;
  rightComponent?: React.ReactNode;
  defaultIsOpen?: boolean;
  stateGroup?: Map<string, boolean>;
  setStateGroup?: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
  analyticsData?: Partial<TrackingData>;
  analyticsActions?: Partial<{ open: string; close: string }>;
  showRightComponentWhenOpen?: boolean;
  flush?: boolean;
  dataTest?: string;
  panelId?: string;
}

export const Accordion: React.FC<AccordionProps> = ({
  children,
  title,
  description,
  rightComponent,
  defaultIsOpen = false,
  stateGroup,
  analyticsData = {},
  analyticsActions = {},
  setStateGroup,
  showRightComponentWhenOpen = false,
  flush = false,
  dataTest,
  panelId,
}) => {
  const { hash } = useLocation();
  const matchesHash = hash.split("#")[1]?.replaceAll("-", " ") === title.toLowerCase();

  const [isOpen, setIsOpen] = useDependentState(matchesHash || defaultIsOpen || stateGroup?.get(title));
  const rightComponentRef = React.useRef<HTMLDivElement>(null);
  const generatedPanelId = React.useId();
  const ownPanelId = panelId ?? generatedPanelId;
  const { trackEvent } = useTracking<TrackingData>({});

  const hashSeeded = React.useRef(false);
  React.useEffect(() => {
    if (!matchesHash || hashSeeded.current || !stateGroup || !setStateGroup) return;
    hashSeeded.current = true;
    if (!stateGroup.get(title)) setStateGroup(stateGroup.set(title, true));
  }, [matchesHash, stateGroup, setStateGroup, title]);

  const handleToggle = (e: React.MouseEvent<HTMLDivElement>) => {
    if (rightComponent && rightComponentRef.current?.contains(e.target as Node)) return;
    setIsOpen(!isOpen);
    let actionString = `${title} accordion ${isOpen ? "closed" : "opened"}`;
    if (analyticsActions.open && !isOpen) {
      actionString = analyticsActions.open;
    } else if (analyticsActions.close && isOpen) {
      actionString = analyticsActions.close;
    }
    trackEvent({
      ...analyticsData,
      action: actionString,
    });
    if (stateGroup && setStateGroup) {
      setStateGroup(stateGroup.set(title, !isOpen));
    }
  };

  const containerClassName = flush
    ? "w-full flex flex-col".concat(matchesHash ? " ring-4 ring-yellow-200" : "")
    : `w-full flex flex-col border rounded ${(isOpen ? "bg-gray-50 border-transparent" : "border-secondary-100").concat(
        matchesHash ? " ring-4 ring-yellow-200" : "",
      )}`;

  return (
    <div className={containerClassName}>
      <div
        onClick={handleToggle}
        data-test={dataTest}
        className={`flex justify-between items-center py-md cursor-pointer ${
          flush ? "border-b border-secondary-100" : "px-lg"
        }`}>
        <div>
          <P size="sm" weight="semiBold" className="text-secondary-900 leading-5">
            {title}
          </P>
          {description && (
            <P size="sm" className="mt-xs text-secondary-500">
              {description}
            </P>
          )}
        </div>
        <div className="flex">
          {(!showRightComponentWhenOpen || isOpen) && rightComponent && (
            <div ref={rightComponentRef} className="accordion-right-component">
              {rightComponent}
            </div>
          )}
          <span aria-hidden="true" className="material-symbols-outlined ml-md text-secondary-900">
            {isOpen ? "expand_less" : "expand_more"}
          </span>
        </div>
      </div>
      {isOpen && (
        <div id={ownPanelId} className={flush ? "" : "px-lg"}>
          {children}
        </div>
      )}
    </div>
  );
};
